import { RequestStatus } from 'features/request/requestSlice';
import type {
    ContractObjectCreateInput,
    ContractObjectUpdateInput,
    ContractSubObjectCreateInput,
    ContractSubObjectUpdateInput,
    WorkOrderChangeObjectSelectionItem,
} from 'gql/graphql';
import type {
    IUseWorkOrderEditObjectDataSourceProps,
    IUseWorkOrderEditObjectDataSourceResult,
} from 'pages/Project/Common/WorkOrderStructure/types';
import { useUerIsUniqueContractObjectCode } from 'pages/Project/UerProject/WorkOrder/WorkOrderStructure/hooks/useUerIsUniqueContractObjectCode';
import { useUerIsUniqueContractObjectName } from 'pages/Project/UerProject/WorkOrder/WorkOrderStructure/hooks/useUerIsUniqueContractObjectName';
import { useUerIsUniqueContractSubObjectName } from 'pages/Project/UerProject/WorkOrder/WorkOrderStructure/hooks/useUerIsUniqueContractSubObjectName';
import { useUerUpdateContractObject } from 'pages/Project/UerProject/WorkOrder/WorkOrderStructure/hooks/useUerUpdateContractObject';
import { useUerUpdateContractSubObject } from 'pages/Project/UerProject/WorkOrder/WorkOrderStructure/hooks/useUerUpdateContractSubObject';

import { useCallback, useEffect, useMemo } from 'react';

import { useUerCreateWorkOrderChangeObject } from './useUerCreateWorkOrderChangeObject';
import { useUerCreateWorkOrderChangeSubObject } from './useUerCreateWorkOrderChangeSubObject';
import { useUerUpdateWorkOrderChangeObject } from './useUerUpdateWorkOrderChangeObject';
import { useUerUpdateWorkOrderChangeSubObject } from './useUerUpdateWorkOrderChangeSubObject';

const nameKey = 'name';
const codeKey = 'codeWBS';
const rationaleKey = 'rationale';

export const useUerWorkOrderChangeEditObjectDataSource = (
    props: IUseWorkOrderEditObjectDataSourceProps<WorkOrderChangeObjectSelectionItem>,
): IUseWorkOrderEditObjectDataSourceResult => {
    const {
        clearErrors,
        setError,
        formState,
        watch,
        constructionTypeId,
        contractId,
        workOrderOrChangeId,
        onCreateSuccess,
        onUpdateSuccess,
        objectId,
        initialCode,
        isContractObject,
        parentObjectToAppend,
    } = props;

    const name = watch?.(nameKey);
    const code = watch?.(codeKey);
    const rationale = watch?.(rationaleKey);
    const isDirtyObjectName = formState?.dirtyFields?.name;
    const isDirtyObjectCode = formState?.dirtyFields?.codeWBS;
    const isClearingCode = !!initialCode?.trim() && !code?.trim();

    const { result: isObjectNameUnique, uerIsUniqueContractObjectNameStatus } = useUerIsUniqueContractObjectName({
        contractId,
        constructionTypeId,
        name: !parentObjectToAppend ? name : '',
        isDirtyObjectName,
    });

    const { result: isSubObjectNameUnique, uerIsUniqueContractSubObjectNameStatus } =
        useUerIsUniqueContractSubObjectName({
            name: parentObjectToAppend ? name : '',
            isDirtyObjectName,
            contractObjectId: parentObjectToAppend?.id,
            contractSubObjectId: objectId,
        });

    const {
        result: isObjectCodeUnique,
        isNotUnique: isObjectCodeNotUnique,
        isCheckError,
    } = useUerIsUniqueContractObjectCode({
        code,
        isDirtyObjectCode,
        contractId,
        contractObjectId: objectId,
    });

    const { uerCreateWorkOrderChangeObjectStatus, uerCreateWorkOrderChangeObjectMakeRequest } =
        useUerCreateWorkOrderChangeObject(workOrderOrChangeId, onCreateSuccess);

    const { uerUpdateWorkOrderChangeObjectStatus, uerUpdateWorkOrderChangeObjectMakeRequest } =
        useUerUpdateWorkOrderChangeObject(workOrderOrChangeId, onUpdateSuccess);

    const { uerCreateWorkOrderChangeSubObjectStatus, uerCreateWorkOrderChangeSubObjectMakeRequest } =
        useUerCreateWorkOrderChangeSubObject(workOrderOrChangeId, onCreateSuccess);

    const { uerUpdateWorkOrderChangeSubObjectStatus, uerUpdateWorkOrderChangeSubObjectMakeRequest } =
        useUerUpdateWorkOrderChangeSubObject(workOrderOrChangeId, onCreateSuccess);

    const { status: uerUpdateContractObjectStatus, makeRequest: uerUpdateContractObjectMakeRequest } =
        useUerUpdateContractObject(contractId, onUpdateSuccess);

    const { uerUpdateContractSubObjectStatus, uerUpdateContractSubObjectMakeRequest } = useUerUpdateContractSubObject(
        contractId,
        onUpdateSuccess,
    );

    const doCreateRequest = useCallback(
        (formData: any) => {
            if (!constructionTypeId) {
                return;
            }

            if (parentObjectToAppend) {
                const input: ContractSubObjectCreateInput = {
                    name: formData.name,
                    rationale: formData.rationale,
                    parentId: parentObjectToAppend.id,
                    codeWBS: formData.codeWBS?.trim(),
                };

                uerCreateWorkOrderChangeSubObjectMakeRequest(input);

                return;
            }

            const input: ContractObjectCreateInput = {
                constructionTypeId,
                contractId,
                name: formData.name,
                rationale: formData.rationale,
                codeWBS: formData.codeWBS?.trim(),
            };

            uerCreateWorkOrderChangeObjectMakeRequest(input);
        },
        [
            constructionTypeId,
            contractId,
            parentObjectToAppend,
            uerCreateWorkOrderChangeObjectMakeRequest,
            uerCreateWorkOrderChangeSubObjectMakeRequest,
        ],
    );

    const doUpdateRequest = useCallback(
        (formData: any) => {
            if (parentObjectToAppend) {
                const input: ContractSubObjectUpdateInput = {
                    id: objectId,
                    name: formData.name,
                    rationale: formData.rationale,
                    codeWBS: formData.codeWBS?.trim(),
                };

                if (isContractObject) {
                    uerUpdateContractSubObjectMakeRequest(input);
                } else {
                    uerUpdateWorkOrderChangeSubObjectMakeRequest(input);
                }

                return;
            }

            const input: ContractObjectUpdateInput = {
                id: objectId,
                name: formData.name,
                rationale: formData.rationale,
                codeWBS: formData.codeWBS?.trim(),
            };

            if (isContractObject) {
                uerUpdateContractObjectMakeRequest(input);
            } else {
                uerUpdateWorkOrderChangeObjectMakeRequest(input);
            }
        },
        [
            isContractObject,
            objectId,
            parentObjectToAppend,
            uerUpdateContractObjectMakeRequest,
            uerUpdateContractSubObjectMakeRequest,
            uerUpdateWorkOrderChangeObjectMakeRequest,
            uerUpdateWorkOrderChangeSubObjectMakeRequest,
        ],
    );

    const isNotUnique = useMemo(() => {
        if (
            !parentObjectToAppend &&
            uerIsUniqueContractObjectNameStatus === RequestStatus.SUCCESS &&
            isDirtyObjectName &&
            !isObjectNameUnique
        ) {
            return true;
        }

        return !!(
            parentObjectToAppend &&
            uerIsUniqueContractSubObjectNameStatus &&
            isDirtyObjectName &&
            !isSubObjectNameUnique
        );
    }, [
        isDirtyObjectName,
        isObjectNameUnique,
        isSubObjectNameUnique,
        parentObjectToAppend,
        uerIsUniqueContractObjectNameStatus,
        uerIsUniqueContractSubObjectNameStatus,
    ]);

    const btnConfirmDisabled = useMemo(
        () =>
            !formState?.isDirty ||
            !name ||
            (!isContractObject && !rationale) ||
            isClearingCode ||
            isNotUnique ||
            !isObjectCodeUnique ||
            uerCreateWorkOrderChangeObjectStatus === RequestStatus.FETCHING ||
            uerUpdateWorkOrderChangeObjectStatus === RequestStatus.FETCHING ||
            uerCreateWorkOrderChangeSubObjectStatus === RequestStatus.FETCHING ||
            uerUpdateWorkOrderChangeSubObjectStatus === RequestStatus.FETCHING ||
            uerUpdateContractObjectStatus === RequestStatus.FETCHING ||
            uerUpdateContractSubObjectStatus === RequestStatus.FETCHING,
        [
            formState?.isDirty,
            isNotUnique,
            isObjectCodeUnique,
            isContractObject,
            isClearingCode,
            name,
            rationale,
            uerCreateWorkOrderChangeObjectStatus,
            uerCreateWorkOrderChangeSubObjectStatus,
            uerUpdateWorkOrderChangeObjectStatus,
            uerUpdateWorkOrderChangeSubObjectStatus,
            uerUpdateContractObjectStatus,
            uerUpdateContractSubObjectStatus,
        ],
    );

    useEffect(() => {
        if (name && isNotUnique) {
            setError?.(nameKey, { message: 'Объект с таким наименованием уже существует' });
        } else {
            clearErrors?.(nameKey);
        }
    }, [clearErrors, name, isNotUnique, setError]);

    useEffect(() => {
        if (isClearingCode) {
            setError?.(codeKey, { message: 'Существующий код нельзя очистить' });
        } else if (code && isObjectCodeNotUnique) {
            setError?.(codeKey, {
                message: `${parentObjectToAppend ? 'Подобъект' : 'Объект'} с таким кодом уже существует`,
            });
        } else if (isCheckError) {
            setError?.(codeKey, { message: 'Не удалось проверить уникальность кода' });
        } else {
            clearErrors?.(codeKey);
        }
    }, [clearErrors, code, isClearingCode, isObjectCodeNotUnique, isCheckError, parentObjectToAppend, setError]);

    return {
        doCreateRequest,
        doUpdateRequest,
        btnConfirmDisabled,
    };
};
